import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { 
  BarChart3, 
  Brain, 
  FileText, 
  Users, 
  ArrowRight, 
  ChevronDown,
  Zap,
  Shield,
  TrendingDown,
  Mail,
  Menu,
  X,
  ExternalLink,
  Newspaper
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cutting-Edge AI Logo - Quantum Core Design
const AILogo = ({ className = "", size = 48 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    
    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, size, size);
      time += 0.03;

      // Outer rotating rings
      for (let ring = 0; ring < 3; ring++) {
        const ringRadius = size * (0.35 + ring * 0.08);
        const ringRotation = time * (ring % 2 === 0 ? 1 : -1) * (1 - ring * 0.2);
        const segments = 6 + ring * 2;
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(ringRotation);
        
        // Draw segmented ring
        for (let i = 0; i < segments; i++) {
          const startAngle = (i / segments) * Math.PI * 2;
          const endAngle = ((i + 0.7) / segments) * Math.PI * 2;
          const alpha = 0.3 + Math.sin(time * 2 + i) * 0.2;
          
          ctx.beginPath();
          ctx.arc(0, 0, ringRadius, startAngle, endAngle);
          ctx.strokeStyle = ring === 2 ? `rgba(0, 240, 255, ${alpha})` : `rgba(123, 97, 255, ${alpha})`;
          ctx.lineWidth = ring === 0 ? 2 : 1.5;
          ctx.lineCap = 'round';
          ctx.stroke();
        }
        
        ctx.restore();
      }

      // Central hexagonal core
      const coreSize = size * 0.22;
      const coreRotation = time * 0.5;
      
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(coreRotation);
      
      // Hexagon path
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(angle) * coreSize;
        const y = Math.sin(angle) * coreSize;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      
      // Hexagon fill with gradient
      const hexGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, coreSize);
      hexGradient.addColorStop(0, 'rgba(123, 97, 255, 0.8)');
      hexGradient.addColorStop(0.5, 'rgba(123, 97, 255, 0.3)');
      hexGradient.addColorStop(1, 'rgba(0, 240, 255, 0.2)');
      ctx.fillStyle = hexGradient;
      ctx.fill();
      
      // Hexagon border
      ctx.strokeStyle = '#00F0FF';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      
      ctx.restore();

      // Inner "K" stylized mark
      ctx.save();
      ctx.translate(centerX, centerY);
      
      const kSize = size * 0.12;
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Stylized K
      ctx.beginPath();
      ctx.moveTo(-kSize * 0.4, -kSize);
      ctx.lineTo(-kSize * 0.4, kSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(-kSize * 0.4, 0);
      ctx.lineTo(kSize * 0.5, -kSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(-kSize * 0.1, -kSize * 0.1);
      ctx.lineTo(kSize * 0.5, kSize);
      ctx.stroke();
      
      ctx.restore();

      // Orbiting data points
      const numOrbiters = 4;
      for (let i = 0; i < numOrbiters; i++) {
        const orbitRadius = size * 0.38;
        const orbitSpeed = 1.5 + i * 0.3;
        const orbitAngle = time * orbitSpeed + (i / numOrbiters) * Math.PI * 2;
        
        const ox = centerX + Math.cos(orbitAngle) * orbitRadius;
        const oy = centerY + Math.sin(orbitAngle) * orbitRadius;
        
        // Data point glow
        const glowGradient = ctx.createRadialGradient(ox, oy, 0, ox, oy, 6);
        glowGradient.addColorStop(0, i % 2 === 0 ? 'rgba(0, 240, 255, 0.9)' : 'rgba(123, 97, 255, 0.9)');
        glowGradient.addColorStop(0.5, i % 2 === 0 ? 'rgba(0, 240, 255, 0.3)' : 'rgba(123, 97, 255, 0.3)');
        glowGradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.arc(ox, oy, 6, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();
        
        // Data point core
        ctx.beginPath();
        ctx.arc(ox, oy, 2, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        
        // Trail
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        const trailAngle = orbitAngle - 0.5;
        const trailX = centerX + Math.cos(trailAngle) * orbitRadius;
        const trailY = centerY + Math.sin(trailAngle) * orbitRadius;
        
        const trailGradient = ctx.createLinearGradient(ox, oy, trailX, trailY);
        trailGradient.addColorStop(0, i % 2 === 0 ? 'rgba(0, 240, 255, 0.6)' : 'rgba(123, 97, 255, 0.6)');
        trailGradient.addColorStop(1, 'transparent');
        
        ctx.strokeStyle = trailGradient;
        ctx.lineWidth = 2;
        ctx.lineTo(trailX, trailY);
        ctx.stroke();
      }

      // Central bright core
      const corePulse = Math.sin(time * 3) * 0.3 + 1;
      const innerCoreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, size * 0.08 * corePulse);
      innerCoreGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      innerCoreGradient.addColorStop(0.3, 'rgba(0, 240, 255, 0.8)');
      innerCoreGradient.addColorStop(0.6, 'rgba(123, 97, 255, 0.4)');
      innerCoreGradient.addColorStop(1, 'transparent');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, size * 0.08 * corePulse, 0, Math.PI * 2);
      ctx.fillStyle = innerCoreGradient;
      ctx.fill();

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size]);

  return (
    <canvas 
      ref={canvasRef} 
      width={size} 
      height={size} 
      className={`${className}`}
      style={{ filter: 'drop-shadow(0 0 12px rgba(0, 240, 255, 0.7))' }}
    />
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-strong py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="logo">
          <AILogo size={48} />
          <span className="font-outfit font-bold text-xl text-white">Kamsikam</span>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <button 
            onClick={() => scrollToSection("services")} 
            className="nav-link text-sm"
            data-testid="nav-services"
          >
            Services
          </button>
          <button 
            onClick={() => scrollToSection("news")} 
            className="nav-link text-sm"
            data-testid="nav-news"
          >
            In The News
          </button>
          <button 
            onClick={() => scrollToSection("how-it-works")} 
            className="nav-link text-sm"
            data-testid="nav-how-it-works"
          >
            How It Works
          </button>
          <button 
            onClick={() => scrollToSection("contact")} 
            className="nav-link text-sm"
            data-testid="nav-contact"
          >
            Contact
          </button>
          <Button 
            onClick={() => scrollToSection("contact")}
            className="btn-primary text-sm"
            data-testid="nav-cta"
          >
            Get Started
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass-strong mt-2 mx-4 rounded-lg p-4 stagger-children" data-testid="mobile-menu">
          <button 
            onClick={() => scrollToSection("services")} 
            className="block w-full text-left py-3 text-white/80 hover:text-white transition-colors"
          >
            Services
          </button>
          <button 
            onClick={() => scrollToSection("news")} 
            className="block w-full text-left py-3 text-white/80 hover:text-white transition-colors"
          >
            In The News
          </button>
          <button 
            onClick={() => scrollToSection("how-it-works")} 
            className="block w-full text-left py-3 text-white/80 hover:text-white transition-colors"
          >
            How It Works
          </button>
          <button 
            onClick={() => scrollToSection("contact")} 
            className="block w-full text-left py-3 text-white/80 hover:text-white transition-colors"
          >
            Contact
          </button>
          <Button 
            onClick={() => scrollToSection("contact")}
            className="btn-primary w-full mt-3"
          >
            Get Started
          </Button>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="hero-section grid-pattern noise-overlay" data-testid="hero-section">
      <div className="hero-bg-gradient" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-main/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent-cyan/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative w-full px-6 lg:px-12 py-32 md:py-0">
        <div className="relative z-10 max-w-xl">
          {/* Left Content */}
          <div className="stagger-children">
            <div className="section-badge">
              <Zap size={16} />
              <span>AI-Powered Cost Optimization</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="hero-title" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
              We will aim to reduce{" "}
              <span className="text-gradient">30%</span> of your operating cost
            </h1>
            
            <p className="text-lg text-secondary mb-8 max-w-lg" style={{ color: 'var(--text-secondary)', textShadow: '0 1px 10px rgba(0,0,0,0.5)' }}>
              Enterprise IT optimization through advanced AI analysis. Make strategic decisions backed by comprehensive reports and actionable insights.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={scrollToContact}
                className="btn-primary"
                data-testid="hero-cta"
              >
                Start Optimization
                <ArrowRight size={18} />
              </Button>
              <Button 
                onClick={() => document.getElementById("services").scrollIntoView({ behavior: "smooth" })}
                className="btn-secondary"
                data-testid="hero-secondary-cta"
              >
                Learn More
                <ChevronDown size={18} />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-gradient">30%</div>
                <div className="text-sm text-muted mt-1" style={{ color: 'var(--text-muted)' }}>Target Savings</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-gradient">4 Weeks</div>
                <div className="text-sm text-muted mt-1" style={{ color: 'var(--text-muted)' }}>To First Insights</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-gradient">AI + Human</div>
                <div className="text-sm text-muted mt-1" style={{ color: 'var(--text-muted)' }}>Led & Assisted</div>
              </div>
            </div>
          </div>
        </div>

          {/* Right Visual - Futuristic Robot - Full right edge */}
          <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none overflow-hidden">
            {/* Glow effects behind the robot */}
            <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-accent-cyan/15 rounded-full blur-3xl" />
            <div className="absolute top-1/2 right-1/3 -translate-y-1/2 w-72 h-72 bg-primary-main/20 rounded-full blur-3xl animate-pulse-slow" />
            
            {/* Robot Image - positioned at right edge */}
            <img 
              src="/robot-hero.png" 
              alt="AI Assistant Robot" 
              className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-auto object-contain"
              style={{ 
                filter: 'drop-shadow(0 0 40px rgba(0, 240, 255, 0.3))',
                maskImage: 'linear-gradient(to left, black 70%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to left, black 70%, transparent 100%)'
              }}
              data-testid="hero-robot-image"
            />
          </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <BarChart3 size={28} />,
      title: "IT Spend Analysis",
      description: "Comprehensive analysis of your IT infrastructure costs, identifying inefficiencies and optimization opportunities."
    },
    {
      icon: <Brain size={28} />,
      title: "AI-Powered Cost Reduction",
      description: "Leverage cutting-edge AI algorithms to automatically identify cost-saving measures across your tech stack."
    },
    {
      icon: <FileText size={28} />,
      title: "Strategic Reporting",
      description: "Detailed reports for executives with actionable insights and ROI projections for informed decision-making."
    },
    {
      icon: <Users size={28} />,
      title: "Vendor Optimization",
      description: "Analyze vendor contracts and relationships to negotiate better terms and consolidate services."
    }
  ];

  return (
    <section id="services" className="py-24 relative" data-testid="services-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-main/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16 stagger-children">
          <div className="section-badge mx-auto w-fit">
            <Zap size={16} />
            <span>Our Services</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Enterprise Cost Optimization
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Comprehensive solutions designed to maximize your IT efficiency and minimize operational costs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 stagger-children">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="service-card hover-lift"
              data-testid={`service-card-${index}`}
            >
              <div className="service-icon" style={{ color: 'var(--accent-cyan)' }}>
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
              <p style={{ color: 'var(--text-secondary)' }}>{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Kaleidoscope News Section
const NewsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const newsItems = [
    {
      company: "Block (Square)",
      leader: "Jack Dorsey",
      headline: "Jack Dorsey Cuts 40% of Block's Workforce Using AI",
      excerpt: "Block announced a massive 40% workforce reduction from 10,000+ to under 6,000 employees, driven by AI tools. Despite strong profits ($10B+ gross profit), Dorsey stated 'a significantly smaller team, using the tools we're building, can do more and do it better.'",
      savings: "40% Workforce Cut",
      metric: "4,000+ Jobs",
      source: "LA Times / Digital Transactions",
      sourceUrl: "https://www.latimes.com/business/story/2026-02-26/block-to-cut-more-than-4-000-jobs",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop",
      color: "#7B61FF"
    },
    {
      company: "Salesforce",
      leader: "Marc Benioff",
      headline: "Salesforce Eliminates 4,000 Support Jobs with AI Agents",
      excerpt: "Marc Benioff cut customer support from 9,000 to 5,000 employees using Agentforce AI. 'Productivity is no longer tied to workforce growth,' Benioff stated, as AI now handles 30-50% of engineering and service tasks.",
      savings: "44% Support Cut",
      metric: "4,000 Jobs",
      source: "Fox Business / CRN",
      sourceUrl: "https://www.foxbusiness.com/economy/salesforce-cuts-4000-jobs-due-ai-ceo-says",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop",
      color: "#00F0FF"
    },
    {
      company: "Amazon",
      leader: "Andy Jassy",
      headline: "Amazon's AI Strategy to Avoid 600,000 Future Hires",
      excerpt: "Amazon plans to automate 75% of operations, potentially avoiding 160,000 hires by 2027 and 600,000 by 2033. Recent layoffs of 30,000 corporate positions save an estimated $6 billion annually to fund $100B+ AI investments.",
      savings: "$6B Annual",
      metric: "600K Jobs Avoided",
      source: "AI Certs / Fox Business",
      sourceUrl: "https://www.foxbusiness.com/media/amazon-defends-ambitious-ai-strategy-could-prevent-600000-future-hires",
      image: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=600&h=400&fit=crop",
      color: "#10B981"
    },
    {
      company: "IBM",
      leader: "Arvind Krishna",
      headline: "IBM Targets $4.5 Billion in AI-Driven Productivity Savings",
      excerpt: "IBM leveraged AI and automation internally since 2023, targeting $4.5 billion in productivity savings by end-2025, saving 3.9 million hours in 2024 through workflow simplification and task automation.",
      savings: "$4.5B Target",
      metric: "3.9M Hours Saved",
      source: "IBM Think Insights",
      sourceUrl: "https://www.ibm.com/think/insights/enterprise-transformation-extreme-productivity-ai",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop",
      color: "#7B61FF"
    },
    {
      company: "Morgan Stanley",
      leader: "James Gorman",
      headline: "Morgan Stanley Saves 80% Time with GPT Assistant",
      excerpt: "Deployed a GPT-powered assistant for financial advisors, automating meeting notes and research to save 10-15 hours weekly per user, achieving up to 80% reduction in report preparation time.",
      savings: "80% Time Cut",
      metric: "15 Hrs/Week",
      source: "AI Realized Now",
      sourceUrl: "https://airealizednow.substack.com/p/from-2024-to-2025-how-enterprise",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop",
      color: "#00F0FF"
    },
    {
      company: "Pfizer",
      leader: "Albert Bourla",
      headline: "Pfizer Cuts Infrastructure Costs by 55% with AI",
      excerpt: "PACT initiative with AWS optimized drug development across 14 projects, saving 16,000 hours of search time annually and cutting infrastructure costs by 55% through AI-powered research optimization.",
      savings: "55% Cost Cut",
      metric: "16K Hours/Year",
      source: "AI Realized Now",
      sourceUrl: "https://airealizednow.substack.com/p/from-2024-to-2025-how-enterprise",
      image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=600&h=400&fit=crop",
      color: "#F43F5E"
    },
    {
      company: "Walmart",
      leader: "Doug McMillon",
      headline: "Walmart Achieves 20% Unit Cost Reduction with AI",
      excerpt: "Applied AI to inventory and supplier negotiations, achieving 1.5% cost reduction in deals, 20% unit cost cuts, and significantly lower inventory expenses across global operations.",
      savings: "20% Unit Cut",
      metric: "Global Scale",
      source: "Coherent Solutions",
      sourceUrl: "https://www.coherentsolutions.com/insights/ai-development-cost-estimation-pricing-structure-roi",
      image: "https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=600&h=400&fit=crop",
      color: "#10B981"
    },
    {
      company: "Siemens",
      leader: "Roland Busch",
      headline: "Siemens Halves Downtime with Predictive AI",
      excerpt: "Used Senseye AI for predictive maintenance, halving equipment downtime and reducing maintenance costs by up to 40% across manufacturing operations worldwide.",
      savings: "40% Maintenance",
      metric: "50% Less Downtime",
      source: "Coherent Solutions",
      sourceUrl: "https://www.coherentsolutions.com/insights/ai-development-cost-estimation-pricing-structure-roi",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop",
      color: "#7B61FF"
    },
    {
      company: "nib Group",
      leader: "Mark Fitzgibbon",
      headline: "nib Group Saves $22 Million with AI Assistant",
      excerpt: "Implemented a virtual assistant handling over 4 million interactions, reducing human support needs by 60%, call volume by 15%, saving $22 million in customer service costs.",
      savings: "$22M Saved",
      metric: "4M+ Interactions",
      source: "Master of Code",
      sourceUrl: "https://masterofcode.com/blog/how-does-ai-reduce-costs",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop",
      color: "#00F0FF"
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % newsItems.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, newsItems.length]);

  const getCardStyle = (index) => {
    const diff = index - activeIndex;
    const totalItems = newsItems.length;
    
    // Normalize diff to handle wrap-around
    let normalizedDiff = diff;
    if (diff > totalItems / 2) normalizedDiff = diff - totalItems;
    if (diff < -totalItems / 2) normalizedDiff = diff + totalItems;
    
    const isActive = normalizedDiff === 0;
    const isAdjacent = Math.abs(normalizedDiff) === 1;
    const isSecondary = Math.abs(normalizedDiff) === 2;
    
    let transform = '';
    let opacity = 0;
    let zIndex = 0;
    let scale = 0.6;
    
    if (isActive) {
      transform = 'translateX(0) rotateY(0deg)';
      opacity = 1;
      zIndex = 30;
      scale = 1;
    } else if (isAdjacent) {
      const direction = normalizedDiff > 0 ? 1 : -1;
      transform = `translateX(${direction * 60}%) rotateY(${-direction * 25}deg)`;
      opacity = 0.7;
      zIndex = 20;
      scale = 0.85;
    } else if (isSecondary) {
      const direction = normalizedDiff > 0 ? 1 : -1;
      transform = `translateX(${direction * 100}%) rotateY(${-direction * 40}deg)`;
      opacity = 0.4;
      zIndex = 10;
      scale = 0.7;
    }
    
    return {
      transform: `${transform} scale(${scale})`,
      opacity,
      zIndex,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
    };
  };

  return (
    <section id="news" className="py-24 relative overflow-hidden" style={{ background: 'var(--background-secondary)' }} data-testid="news-section">
      {/* Kaleidoscope background effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-main/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent-cyan/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16">
          <div className="section-badge mx-auto w-fit">
            <Newspaper size={16} />
            <span>In The News</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Tech Giants Saving Billions with AI
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Real stories of how industry leaders are transforming their workforce and operations with AI.
          </p>
        </div>

        {/* Kaleidoscope Carousel */}
        <div 
          className="relative h-[500px] perspective-1000"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            {newsItems.map((item, index) => {
              const style = getCardStyle(index);
              const isActive = index === activeIndex;
              
              return (
                <div
                  key={index}
                  className="absolute w-full max-w-xl cursor-pointer"
                  style={style}
                  onClick={() => setActiveIndex(index)}
                  data-testid={`news-card-${index}`}
                >
                  <div 
                    className={`kaleidoscope-card glass rounded-2xl overflow-hidden ${isActive ? 'ring-2' : ''}`}
                    style={{ 
                      '--card-color': item.color,
                      ringColor: item.color
                    }}
                  >
                    {/* Image Header */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.company}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                      
                      {/* Company Badge */}
                      <div className="absolute top-4 left-4">
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: item.color }}
                        >
                          {item.company}
                        </span>
                      </div>
                      
                      {/* Savings Badge */}
                      <div className="absolute bottom-4 right-4 text-right">
                        <div className="text-2xl font-bold text-white">{item.savings}</div>
                        <div className="text-xs text-white/70">{item.metric}</div>
                      </div>
                      
                      {/* Leader */}
                      <div className="absolute bottom-4 left-4">
                        <div className="text-xs text-white/60">Led by</div>
                        <div className="text-sm font-semibold text-white">{item.leader}</div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-3 leading-tight">
                        {item.headline}
                      </h3>
                      <p className="text-sm mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                        {item.excerpt}
                      </p>
                      <a 
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium transition-colors"
                        style={{ color: item.color }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.source}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {newsItems.map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === activeIndex 
                  ? 'w-8' 
                  : 'opacity-50 hover:opacity-80'
              }`}
              style={{ 
                background: index === activeIndex ? item.color : 'var(--text-muted)'
              }}
              data-testid={`news-dot-${index}`}
            />
          ))}
        </div>

        {/* Industry Statistics */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Industry Impact 2024-2026
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gradient">$4.4T</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>AI Market by 2028</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gradient">40%</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Avg. Cost Reduction</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gradient">72%</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Enterprises Adopting AI</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gradient">10x</div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Avg. ROI on AI Projects</div>
            </div>
          </div>
          <div className="text-center mt-4">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Sources: McKinsey, Gartner, Forbes 2024-2025 Reports
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Data Collection",
      description: "We spend time with you to deeply understand your business, challenges, and goals while collecting all relevant data for analysis."
    },
    {
      number: "02",
      title: "AI Analysis",
      description: "Our advanced AI algorithms analyze patterns, identify inefficiencies, and calculate optimization potential."
    },
    {
      number: "03",
      title: "Report Generation",
      description: "Receive detailed reports with prioritized recommendations and projected savings."
    },
    {
      number: "04",
      title: "Execution & Savings",
      description: "Implement recommendations with our support and track your cost reductions in real-time."
    }
  ];

  return (
    <section id="how-it-works" className="py-24 relative" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 stagger-children">
          <div className="section-badge mx-auto w-fit">
            <Zap size={16} />
            <span>Process</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            How It Works
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            A streamlined process to identify and capture cost savings across your organization.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="relative"
              data-testid={`step-${index}`}
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-primary-main to-transparent" />
              )}
              
              <div className="glass rounded-xl p-6 hover-lift h-full">
                <div 
                  className="text-4xl font-bold mb-4 opacity-30"
                  style={{ color: 'var(--primary-main)' }}
                >
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p style={{ color: 'var(--text-secondary)' }}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    it_spend_range: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, it_spend_range: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        toast.success("Message sent successfully!", {
          description: "We'll get back to you within 24 hours."
        });
        setFormData({
          name: "",
          email: "",
          company: "",
          it_spend_range: "",
          message: ""
        });
      }
    } catch (error) {
      let errorMessage = "Please try again later.";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => 
            typeof err === 'object' ? (err.msg || err.message || JSON.stringify(err)) : String(err)
          ).join(", ");
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          errorMessage = String(detail);
        }
      }
      
      toast.error("Failed to send message", {
        description: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative" style={{ background: 'var(--background-secondary)' }} data-testid="contact-section">
      <div className="absolute inset-0 bg-gradient-to-t from-primary-main/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div className="stagger-children">
            <div className="section-badge">
              <Mail size={16} />
              <span>Get In Touch</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Optimize Your IT Costs?
            </h2>
            <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
              Fill out the form and our team will reach out within 24 hours to discuss how we can help reduce your operating costs.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="service-icon w-12 h-12 flex-shrink-0">
                  <Shield size={24} style={{ color: 'var(--accent-cyan)' }} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Enterprise Security</h4>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Your data is protected with enterprise-grade encryption
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="service-icon w-12 h-12 flex-shrink-0">
                  <Zap size={24} style={{ color: 'var(--accent-cyan)' }} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Quick Implementation</h4>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    See results within weeks, not months
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="service-icon w-12 h-12 flex-shrink-0">
                  <TrendingDown size={24} style={{ color: 'var(--accent-cyan)' }} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Proven Methodology</h4>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Our approach consistently delivers on the 30% cost reduction target
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="glass rounded-2xl p-8 tracing-border" data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Full Name
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="form-input"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Company Email
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  required
                  className="form-input"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Company Name
                </label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Acme Corporation"
                  required
                  className="form-input"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your IT challenges and goals..."
                  required
                  rows={4}
                  className="form-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Annual IT Spend (Optional)
                </label>
                <Select onValueChange={handleSelectChange} value={formData.it_spend_range}>
                  <SelectTrigger className="form-input" data-testid="contact-spend-select">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent 
                    className="z-[100] bg-[#0B0F19] border border-white/10" 
                    position="popper" 
                    side="top"
                    sideOffset={8}
                  >
                    <SelectItem value="under-500k">Under $500K</SelectItem>
                    <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                    <SelectItem value="1m-5m">$1M - $5M</SelectItem>
                    <SelectItem value="5m-10m">$5M - $10M</SelectItem>
                    <SelectItem value="over-10m">Over $10M</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                <ArrowRight size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <AILogo size={40} />
            <span className="font-outfit font-bold text-xl">Kamsikam</span>
          </div>
          
          <p className="text-sm text-center md:text-left" style={{ color: 'var(--text-muted)' }}>
            © {currentYear} Kamsikam. A subsidiary of{' '}
            <a 
              href="https://intellismart.cloud" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              style={{ color: 'var(--accent-cyan)' }}
            >
              Intellismart.cloud
            </a>
            . All rights reserved.
          </p>
          
          <div className="flex items-center gap-6">
            <a href="#" className="text-sm hover:text-white transition-colors" style={{ color: 'var(--text-muted)' }}>
              Privacy Policy
            </a>
            <a href="#" className="text-sm hover:text-white transition-colors" style={{ color: 'var(--text-muted)' }}>
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" style={{ background: 'var(--background-default)' }}>
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <NewsSection />
        <HowItWorksSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
